import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  createMessageTemplate,
  CreateMessageTemplatePayload,
  getMessageTemplateList,
  MessageTemplate,
  updateMessageTemplate,
  UpdateMessageTemplatePayload,
} from "../../apis/messageTemplateApis";

/* ================= STATE ================= */

interface MessageTemplateState {
  templates: MessageTemplate[];
  loading: boolean;
  error: string | null;
}

const initialState: MessageTemplateState = {
  templates: [],
  loading: false,
  error: null,
};

/* ================= THUNK ================= */
/* (UNCHANGED — exactly as you provided) */

export const fetchMessageTemplateListThunk = createAsyncThunk<
  MessageTemplate[],
  void,
  { rejectValue: string }
>("messageTemplate/fetchList", async (_, { rejectWithValue }) => {
  try {
    const response = await getMessageTemplateList();

    if (!response.status) {
      return rejectWithValue("Failed to fetch message templates");
    }

    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.message || "Failed to fetch message templates",
    );
  }
});

/* ================= CREATE THUNK ================= */

export const createMessageTemplateThunk = createAsyncThunk<
  MessageTemplate,
  CreateMessageTemplatePayload,
  { rejectValue: string }
>("messageTemplate/create", async (payload, { rejectWithValue }) => {
  try {
    const response = await createMessageTemplate(payload);

    if (!response.status) {
      return rejectWithValue("Failed to create message template");
    }

    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.message || "Failed to create message template",
    );
  }
});

/* ================= UPDATE THUNK ================= */

export const updateMessageTemplateThunk = createAsyncThunk<
  MessageTemplate,
  UpdateMessageTemplatePayload,
  { rejectValue: string }
>("messageTemplate/update", async (payload, { rejectWithValue }) => {
  try {
    const response = await updateMessageTemplate(payload);

    if (!response.status) {
      return rejectWithValue("Failed to update message template");
    }

    return response.data[0];
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.message || "Failed to update message template",
    );
  }
});

/* ================= SLICE ================= */

const messageTemplateSlice = createSlice({
  name: "messageTemplate",
  initialState,
  reducers: {
    resetMessageTemplateState: () => initialState,
  },
  extraReducers: (builder) => {
    const getErrorMessage = (payload: unknown, fallback: string) =>
      typeof payload === "string" ? payload : fallback;

    builder
      /* ===== FETCH ===== */
      .addCase(fetchMessageTemplateListThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMessageTemplateListThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.templates = action.payload;
      })
      .addCase(fetchMessageTemplateListThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = getErrorMessage(
          action.payload,
          "Failed to fetch message templates",
        );
        toast.error(state.error);
      })

      /* ===== CREATE ===== */
      .addCase(createMessageTemplateThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMessageTemplateThunk.fulfilled, (state, action) => {
        state.loading = false;

        state.templates.unshift(action.payload);

        toast.success("Message template created successfully");
      })
      .addCase(createMessageTemplateThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = getErrorMessage(
          action.payload,
          "Failed to create message template",
        );
        toast.error(state.error);
      })

      /* ===== UPDATE ===== */
      .addCase(updateMessageTemplateThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMessageTemplateThunk.fulfilled, (state, action) => {
        state.loading = false;

        const updatedTemplate = action.payload;

        const index = state.templates.findIndex(
          (item) => item.id === updatedTemplate.id,
        );

        if (index !== -1) {
          state.templates[index] = updatedTemplate;
        }

        toast.success("Message template updated successfully");
      })
      .addCase(updateMessageTemplateThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = getErrorMessage(
          action.payload,
          "Failed to update message template",
        );
        toast.error(state.error);
      });
  },
});

/* ================= EXPORTS ================= */

export const { resetMessageTemplateState } = messageTemplateSlice.actions;

export default messageTemplateSlice.reducer;
